/**
 * Counts down to a real dispatch cut-off.
 *
 * The deadline is an absolute epoch computed in Liquid from the shop's timezone, so the
 * countdown is timezone-independent and honest — when it reaches zero the element swaps to
 * the "missed the cut-off" message rather than resetting to invent fresh urgency.
 */
class DeliveryPromise extends HTMLElement {
  /** @type {number} */
  deadline = 0;

  /** @type {Element[]} */
  countdownEls = [];

  /** @type {Element | undefined} */
  countdownEl;

  /** @type {Element | null} */
  liveRegion = null;

  /** @type {HTMLElement | null} */
  missedEl = null;

  /** @type {ReturnType<typeof setInterval> | undefined} */
  timer;

  connectedCallback() {
    this.deadline = Number(this.dataset.deadline) * 1000;
    this.countdownEls = [...this.querySelectorAll('[data-countdown]')];
    this.countdownEl = this.countdownEls[0];
    this.liveRegion = this.querySelector('[data-countdown-region]');
    this.missedEl = /** @type {HTMLElement | null} */ (this.querySelector('[data-cutoff-missed]'));

    if (!this.countdownEl || !Number.isFinite(this.deadline) || this.deadline <= 0) return;

    this.#tick();
    this.timer = setInterval(() => this.#tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  #tick() {
    const remaining = this.deadline - Date.now();

    if (remaining <= 0) {
      clearInterval(this.timer);
      this.#expire();
      return;
    }

    const hours = Math.floor(remaining / 3600000);
    const minutes = Math.floor((remaining % 3600000) / 60000);
    const seconds = Math.floor((remaining % 60000) / 1000);
    /** @param {number} n */
    const pad = (n) => String(n).padStart(2, '0');

    if (this.dataset.format === 'hm') {
      // Minute precision: "3h 12m". Under a minute left, say so rather than showing "0m".
      this.#write(
        hours > 0 ? `${hours}h ${pad(minutes)}m` : minutes > 0 ? `${minutes}m` : `${seconds}s`
      );
    } else {
      this.#write(
        hours > 0 ? `${hours}h ${pad(minutes)}m ${pad(seconds)}s` : `${minutes}m ${pad(seconds)}s`
      );
    }

    // Announce only on the minute so screen readers aren't flooded once a second.
    if (this.liveRegion && seconds === 0) {
      this.liveRegion.textContent =
        hours > 0 ? `${hours} hours ${minutes} minutes left to order` : `${minutes} minutes left to order`;
    }
  }

  /** @param {string} text */
  #write(text) {
    for (const el of this.countdownEls) el.textContent = text;
  }

  #expire() {
    const promise = /** @type {HTMLElement | null} */ (this.querySelector('[data-cutoff-promise]'));
    if (promise) promise.hidden = true;
    if (this.missedEl) this.missedEl.hidden = false;
  }
}

if (!customElements.get('delivery-promise')) {
  customElements.define('delivery-promise', DeliveryPromise);
}
